import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * Site-wide stream playback.
 *
 * One `<audio>` element lives here for the whole app, so playback survives
 * navigation between pages and any number of player UIs can drive it. The
 * pieces on screen are dumb: the inline player on a station page and the
 * docked bar at the bottom of the viewport both read this context (see
 * components/StreamPlayer).
 */

export type PlaybackStatus = "idle" | "loading" | "playing" | "error";

/** What is loaded into the player. */
export interface StreamSource {
  /** Stable id — changing it swaps the stream. */
  id: string;
  name: string;
  streamUrl: string;
  logoUrl?: string;
  /** Where the docked bar's title links back to. */
  href?: string;
}

/** Metadata for display only; the page that polls the station feeds it in. */
export interface NowPlaying {
  show?: string;
  track?: string;
  artist?: string;
}

export interface StreamContextValue {
  source: StreamSource | null;
  status: PlaybackStatus;
  nowPlaying: NowPlaying;
  /** True when playback is loaded but the inline player is off screen. */
  isDocked: boolean;
  /**
   * True when the site is on https but the stream is not: browsers block the
   * request as mixed content, so there is no point offering play.
   */
  isBlockedMixedContent: boolean;
  play: () => void;
  stop: () => void;
  toggle: () => void;
  setSource: (source: StreamSource | null) => void;
  setNowPlaying: (nowPlaying: NowPlaying) => void;
  /** Ref callback for the inline player, so the bar knows when to take over. */
  anchorRef: (element: HTMLElement | null) => void;
}

const isMixedContent = (streamUrl: string): boolean =>
  typeof window !== "undefined" &&
  window.location.protocol === "https:" &&
  streamUrl.startsWith("http:");

const StreamContext = createContext<StreamContextValue | null>(null);

export const StreamProvider = ({ children }: { children: ReactNode }) => {
  // Source and metadata move together: swapping streams must not leave the
  // previous station's track on screen, and a page that sets both in one go
  // must not have its metadata wiped by the swap.
  const [{ source, nowPlaying }, setPlayer] = useState<{
    source: StreamSource | null;
    nowPlaying: NowPlaying;
  }>({ source: null, nowPlaying: {} });
  const [status, setStatus] = useState<PlaybackStatus>("idle");
  // The bar takes over once the listener has engaged with the player at least
  // once — pausing from the bar should not make the bar vanish.
  const [hasEngaged, setHasEngaged] = useState(false);
  const [anchorVisible, setAnchorVisible] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    // Playback rides at full scale; the listener's own hardware and OS are
    // the level controls, so there is nothing here to set or remember.
    const audio = new Audio();
    audio.preload = "none";
    audioRef.current = audio;

    const onPlaying = () => setStatus("playing");
    const onWaiting = () => setStatus("loading");
    // Stopping detaches the source (see `stop`), which some browsers report as
    // a media error; only a stream we are actually holding can fail.
    const onError = () => {
      if (audio.hasAttribute("src")) {
        setStatus("error");
      }
    };
    audio.addEventListener("playing", onPlaying);
    audio.addEventListener("waiting", onWaiting);
    audio.addEventListener("error", onError);

    return () => {
      audio.removeEventListener("playing", onPlaying);
      audio.removeEventListener("waiting", onWaiting);
      audio.removeEventListener("error", onError);
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
      audioRef.current = null;
    };
  }, []);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      // A live stream keeps filling its buffer while merely paused, and
      // resumes minutes behind. Dropping the source ends the connection so
      // the next play starts at the live edge.
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
    }
    setStatus("idle");
  }, []);

  const play = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !source?.streamUrl || isMixedContent(source.streamUrl)) {
      return;
    }
    setHasEngaged(true);
    setStatus("loading");
    audio.src = source.streamUrl;
    audio.play().catch((error: Error) => {
      // Autoplay rejections land here too; either way there is no audio.
      if (error.name !== "AbortError") {
        setStatus("error");
      }
    });
  }, [source]);

  const toggle = useCallback(() => {
    if (status === "idle" || status === "error") {
      play();
    } else {
      stop();
    }
  }, [status, play, stop]);

  const setSource = useCallback((next: StreamSource | null) => {
    // Identity is (id, url): callers re-declare their source on every render,
    // so bail out unless the stream itself actually changed.
    setPlayer((current) =>
      current.source?.id === next?.id &&
      current.source?.streamUrl === next?.streamUrl
        ? current
        : { source: next, nowPlaying: {} },
    );
  }, []);

  const setNowPlaying = useCallback((next: NowPlaying) => {
    setPlayer((current) => ({ ...current, nowPlaying: next }));
  }, []);

  // A different stream means whatever was loaded is no longer playing.
  const sourceKey = `${source?.id ?? ""} ${source?.streamUrl ?? ""}`;
  useEffect(() => {
    stop();
    setHasEngaged(false);
  }, [sourceKey, stop]);

  // Watch the inline player: once it scrolls away, the docked bar takes over.
  const observerRef = useRef<IntersectionObserver | null>(null);
  const anchorRef = useCallback((element: HTMLElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!element) {
      setAnchorVisible(false);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setAnchorVisible(entry.isIntersecting),
      { threshold: 0.35 },
    );
    observer.observe(element);
    observerRef.current = observer;
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  const value = useMemo<StreamContextValue>(
    () => ({
      source,
      status,
      nowPlaying,
      isDocked: hasEngaged && !anchorVisible,
      isBlockedMixedContent: source ? isMixedContent(source.streamUrl) : false,
      play,
      stop,
      toggle,
      setSource,
      setNowPlaying,
      anchorRef,
    }),
    [
      source,
      status,
      nowPlaying,
      hasEngaged,
      anchorVisible,
      play,
      stop,
      toggle,
      setSource,
      setNowPlaying,
      anchorRef,
    ],
  );

  return (
    <StreamContext.Provider value={value}>{children}</StreamContext.Provider>
  );
};

export const useStream = (): StreamContextValue => {
  const context = useContext(StreamContext);
  if (!context) {
    throw new Error("useStream must be used inside a StreamProvider");
  }
  return context;
};
